import { useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { TreeItem } from "@mui/x-tree-view";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { observer } from "mobx-react";
import { useViewState } from "../model/ViewState";
import { useProjectStore } from "../model/ProjectStore";

const boxHeight = 25;

const TreeItemPackage = ({ pack, children, movePackage }) => {
  const project = useProjectStore();
  const view = useViewState();
  const [location, setLocation] = useState("middle");
  const ref = useRef(null);
  const currentType = "package";

  const [{ isDragging }, drag] = useDrag({
    type: "package",
    item: () => {
      return {
        idPack: pack.displayIndex,
        locationPack: "middle",
      }
    },
    end: (item, monitor) => {
      const type = monitor.getDropResult();
      if (type === null || monitor.getItem() === null) {
        return;
      }

      movePackage(
        pack.displayIndex,
        monitor.getItem().idPack,
        monitor.getItem().locationPack
      );
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging()
    })
  });

  const [{ isOverCurrent }, drop] = useDrop({
    accept: ["task", "package"],
    hover: (item, monitor) => {
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      let hoverBoxHeight = hoverBoundingRect.bottom - hoverBoundingRect.top;

      if (hoverBoxHeight / boxHeight >= 2) {
        hoverBoxHeight = boxHeight;
      }

      const middleStart = hoverBoxHeight / 4;
      const middleEnd = (3 * hoverBoxHeight) / 4;

      const clientOffset = monitor.getClientOffset();
      const distTop = clientOffset.y - hoverBoundingRect.top;

      // insertAbove
      if (location !== "top" && distTop <= middleStart) {
        monitor.getItem().idPack = pack.displayIndex;
        monitor.getItem().locationPack = "top";
        setLocation("top");
      }
      // insert into package
      else if (location !== "middle" && middleStart < distTop && distTop < middleEnd) {
        monitor.getItem().idPack = pack.displayIndex;
        monitor.getItem().locationPack = "middle";
        setLocation("middle");
      }

      // insert Below
      else if (location !== "bottom" && middleEnd <= distTop && distTop <= hoverBoxHeight) {
        monitor.getItem().idPack = pack.displayIndex;
        monitor.getItem().locationPack = "bottom";
        setLocation("bottom");
      }
    },
    drop: (item, monitor) => {
      if (!monitor.didDrop()) {
        return {
          dropType: currentType,
        };
      }
    },
    collect: (monitor) => {
      return {
        isOverCurrent: !!monitor.isOver({ shallow: true })
      }
    }
  });

  const opacity = isDragging ? 0 : 1;
  const backgroundColor = isOverCurrent && location === "middle" ? "darkGrey" : "white";

  const paddingTop = isOverCurrent && location === "top" ? <Divider style={{ background: "black" }} /> : <div />;
  const paddingBottom =
    isOverCurrent && location === "bottom" ? <Divider style={{ background: "black" }} /> : <div />;

  const label = pack.code + " " + pack.name;

  const handlePackageClick = (event) => {
    if (event.target.closest('.MuiTreeItem-iconContainer')) {
      return;
    }

    event.stopPropagation();
    view.setSelectedTaskId(pack.id)
    view.setSelectedTaskIndex(project.rootTaskPackage.getDisplayIndex(pack.id));
  }

  return (
    <div ref={(node) => { ref.current = node; drag(drop(node)) }}>
      {paddingTop}
      <TreeItem
        style={{ background: backgroundColor, opacity }}
        itemId={pack.id}
        label={<Typography variant="body1" style={{ fontWeight: "normal" }}>{label}</Typography>}
        onClick={handlePackageClick}
      >
        {children}
      </TreeItem>
      {paddingBottom}
    </div>
  );
};

export default observer(TreeItemPackage);